import { useGetSubscription } from "@/api/stripe";
import { useUserInfo } from "@/api/user";
import { ROUTES } from "@/constants/routes";
import { useOpenPaymentMethodDialog } from "@/context/PaymentMethodDialogContext";
import {
  paymentMethodSetupQuery,
  type PaymentMethodIntent,
} from "@/utils/confirmSetup";
import { route } from "@/utils/route";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Adds or updates the subscription's payment method, carrying the current
 * plan/interval/seat count so the write submits the right subscription. When a
 * default payment method already exists it enters payment_method_update mode;
 * otherwise it's a fresh setup. This is the single source of truth for the
 * "add payment method" action — banners and the usage billing page share it so
 * the flow stays identical everywhere. Opens the dialog the settings shell
 * mounts when there is one, and falls back to the checkout page when there is
 * not.
 *
 * Reads the subscription from getSubscription, falling back to /me's
 * subscription. The banners and usage gate that trigger this action derive
 * their visibility from /me, which can report a trialing subscription while
 * getSubscription is still loading or 404ing (it 404s for new orgs). Without
 * the fallback the button would silently no-op in that window.
 */
export function useStartPaymentMethodSetup() {
  const navigate = useNavigate();
  const { data: subscription } = useGetSubscription();
  const { data: userInfo } = useUserInfo();
  const openPaymentMethodDialog = useOpenPaymentMethodDialog();

  return useCallback(
    (options?: { redirectTo?: string; keepTrial?: boolean }) => {
      const current =
        subscription?.data.stripe_subscription ?? userInfo?.subscription;
      if (!current) {
        return;
      }

      // Derived once and shared by both branches — the anti-drift point.
      const redirectTo = options?.redirectTo ?? ROUTES.SETTINGS.USAGE_BILLING;
      const intent: PaymentMethodIntent = {
        planName: current.plan_name,
        billingCycle: current.billing_interval,
        userCount: current.seat_count,
        // Only existing payment methods are "updated"; a first-time setup omits
        // the mode so checkout runs its create-subscription branch.
        isPaymentMethodUpdate: Boolean(current.default_payment_method_id),
        // keepTrial preserves remaining trial days when a card is added to a
        // still-running trial. Omitted when the intent is to convert now.
        // Note: the trial *gate* (useRequireSubscription) never reaches here —
        // it bounces to /billing/checkout before the workspace mounts.
        keepTrial: Boolean(options?.keepTrial) && current.status === "trialing",
      };

      // Inside the workspace shell the card is collected in a dialog; outside it
      // there is nowhere to stack one, so checkout still owns it.
      if (openPaymentMethodDialog) {
        openPaymentMethodDialog({
          ...intent,
          planName: current.plan_name,
          billingCycle: current.billing_interval,
          userCount: current.seat_count,
          redirectTo,
        });
        return;
      }

      navigate(
        route(
          ROUTES.BILLING.CHECKOUT,
          {},
          paymentMethodSetupQuery(intent, redirectTo),
        ),
      );
    },
    [navigate, openPaymentMethodDialog, subscription, userInfo],
  );
}
